import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Stack, Lightning, ChartLineUp, ArrowRight, ArrowLeft,
  CheckCircle, Info, Database, Play, CircleNotch,
  SealCheck, DownloadSimple, Sparkle,
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ErrorBar, Cell } from 'recharts';
import { getTissueImage } from '../../utils/tissueImageMapping';

// ─── types ────────────────────────────────────────────────────────────────────

interface MockDataset {
  id: string; geoId: string; title: string; description: string;
  samples: number; platform: string; size: string; genes: number;
}

interface MockSignature {
  id: string; name: string; tissue: string; tissueKey: string;
  imageUrl?: string; cellTypes: number; genes: number;
  organism: string; version: string;
  hierarchy: { category: string; types: string[] }[];
}

interface CellTypeRow { name: string; key: string; mean: number; sd: number; color: string; }
type SampleRow = { name: string; cluster: number; [cellType: string]: string | number };

interface SigResults {
  cellTypes: CellTypeRow[];
  samples: SampleRow[];
  hmColMax: Record<string, number>;
  hmHeaders: string[];
  clusterLabels: Record<number, string>;
  clusters: number[];
  dominantLabel: string;
  dominantSub: string;
  correlation: string;
  completionMsg: string;
  chromeInfo: string;
  qualityMetrics: { label: string; value: string }[];
  logLines: string[];
  reportItems: { section: string; title: string; sub: string }[];
}

// ─── datasets ─────────────────────────────────────────────────────────────────

const mockDatasets: MockDataset[] = [
  {
    id: 'gse1', geoId: 'GSE178709', title: 'PBMC — COVID-19 severity cohort',
    description: 'Immune profiling across asymptomatic, mild, and severe COVID-19 patients to characterise disease-stage immune shifts.',
    samples: 15, platform: 'RNA-seq', size: '2.3 MB', genes: 18342,
  },
  {
    id: 'gse2', geoId: 'GSE176021', title: 'Lung NSCLC tumor microenvironment',
    description: 'Bulk RNA-seq of treatment-naïve NSCLC biopsies capturing the full cellular landscape of the tumor microenvironment.',
    samples: 28, platform: 'RNA-seq', size: '5.1 MB', genes: 31204,
  },
  {
    id: 'gse3', geoId: 'GSE147507', title: 'Brain GBM infiltrating immune cells',
    description: 'Microarray profiling of glioblastoma resections to quantify myeloid and lymphoid immune infiltration patterns.',
    samples: 9, platform: 'Microarray', size: '1.7 MB', genes: 22417,
  },
];

const RECOMMENDED_SIGS: Record<string, string> = {
  gse1: 'pbmc-covid',
  gse2: 'lung-tme',
  gse3: 'brain-atlas',
};

const RECOMMENDED_REASON: Record<string, string> = {
  'pbmc-covid': 'Matched: Blood · COVID-19 inflammatory context',
  'lung-tme':   'Matched: Lung · NSCLC tumor microenvironment',
  'brain-atlas': 'Matched: Brain · Immune infiltration profile',
};

// ─── signatures (trimmed to 5 for demo clarity) ───────────────────────────────

const mockSignatures: MockSignature[] = [
  {
    id: 'pbmc-covid', name: 'PBMC COVID-19', tissue: 'Blood (inflamed)', tissueKey: 'blood',
    cellTypes: 10, genes: 19204, organism: 'Human', version: 'v1.2',
    hierarchy: [
      { category: 'T Cells', types: ['CD4+ activated', 'CD8+ cytotoxic', 'Tregs (expanded)'] },
      { category: 'Myeloid (inflamed)', types: ['Inflammatory monocytes', 'Low-density granulocytes', 'pDC (depleted)'] },
      { category: 'B Lineage', types: ['Plasmablasts (expanded)', 'Memory B cells', 'Naive B cells'] },
      { category: 'NK', types: ['Activated NK cells'] },
    ],
  },
  {
    id: 'pbmc-v3', name: 'PBMC Immune', tissue: 'Blood', tissueKey: 'blood',
    cellTypes: 8, genes: 23847, organism: 'Human', version: 'v3.1',
    hierarchy: [
      { category: 'T Cells', types: ['CD4+ helper T', 'CD8+ cytotoxic T', 'Regulatory T (Treg)'] },
      { category: 'Innate', types: ['NK cells', 'Classical monocytes', 'Non-classical monocytes'] },
      { category: 'B Lineage', types: ['Naive B cells', 'Memory B cells'] },
    ],
  },
  {
    id: 'lung-tme', name: 'LungTME — NSCLC', tissue: 'Lung', tissueKey: 'lung',
    cellTypes: 14, genes: 31204, organism: 'Human', version: 'v2.0',
    hierarchy: [
      { category: 'Malignant', types: ['Tumor epithelial (NSCLC)'] },
      { category: 'T / NK', types: ['CD4+ T', 'CD8+ T (exhausted)', 'Tregs', 'NK cells'] },
      { category: 'Myeloid', types: ['TAMs (M2-like)', 'MDSCs', 'DCs', 'Neutrophils'] },
      { category: 'Stromal', types: ['CAFs', 'Endothelial', 'Pericytes', 'Alveolar cells'] },
    ],
  },
  {
    id: 'brain-atlas', name: 'BrainCell Atlas', tissue: 'Brain', tissueKey: 'brain',
    cellTypes: 11, genes: 19382, organism: 'Human', version: 'v2.0',
    hierarchy: [
      { category: 'Neurons', types: ['Excitatory (L2/3, L4, L5)', 'Inhibitory (PV+, SST+, VIP+)'] },
      { category: 'Glia', types: ['Astrocytes', 'Oligodendrocytes', 'OPCs', 'Microglia'] },
      { category: 'Vascular', types: ['Endothelial', 'Pericytes', 'Smooth muscle cells'] },
    ],
  },
  {
    id: 'liver-hcc', name: 'LiverCell — HCC', tissue: 'Liver', tissueKey: 'liver',
    cellTypes: 9, genes: 26731, organism: 'Human', version: 'v1.3',
    hierarchy: [
      { category: 'Malignant', types: ['HCC tumor cells'] },
      { category: 'Immune', types: ['Kupffer cells', 'TAMs', 'CD8+ TILs', 'Tregs'] },
      { category: 'Stromal', types: ['Hepatic stellate cells', 'LSECs', 'Portal endothelium', 'CAFs'] },
    ],
  },
];

// ─── per-signature result sets ────────────────────────────────────────────────

const CLUSTER_COLORS: Record<number, string> = {
  1: 'hsl(182, 58%, 42%)',
  2: 'hsl(38, 72%, 52%)',
  3: 'hsl(220, 62%, 52%)',
};

const SIGNATURE_RESULTS: Record<string, SigResults> = {

  'pbmc-covid': {
    cellTypes: [
      { name: 'Inflammatory monocytes', key: 'imono',  mean: 0.284, sd: 0.092, color: 'hsl(38, 68%, 48%)' },
      { name: 'CD4+ activated T',       key: 'cd4',    mean: 0.221, sd: 0.068, color: 'hsl(182, 72%, 28%)' },
      { name: 'CD8+ cytotoxic T',       key: 'cd8',    mean: 0.183, sd: 0.051, color: 'hsl(201, 80%, 42%)' },
      { name: 'Plasmablasts',           key: 'plasma', mean: 0.127, sd: 0.084, color: 'hsl(200, 68%, 56%)' },
      { name: 'Activated NK cells',     key: 'nk',     mean: 0.103, sd: 0.042, color: 'hsl(160, 55%, 48%)' },
      { name: 'Tregs (expanded)',        key: 'treg',   mean: 0.082, sd: 0.031, color: 'hsl(182, 55%, 52%)' },
    ],
    samples: [
      { name: 'GSM3301', cluster: 1, imono: 12.4, cd4: 31.2, cd8: 24.7, plasma:  4.3, nk: 16.8, treg: 10.6 },
      { name: 'GSM3302', cluster: 1, imono: 15.1, cd4: 28.6, cd8: 22.3, plasma:  5.8, nk: 17.4, treg: 10.8 },
      { name: 'GSM3303', cluster: 1, imono: 11.8, cd4: 33.4, cd8: 25.6, plasma:  3.9, nk: 15.3, treg: 10.0 },
      { name: 'GSM3304', cluster: 1, imono: 14.3, cd4: 29.8, cd8: 23.4, plasma:  6.2, nk: 16.1, treg: 10.2 },
      { name: 'GSM3305', cluster: 1, imono: 13.7, cd4: 30.1, cd8: 24.8, plasma:  4.7, nk: 16.7, treg: 10.0 },
      { name: 'GSM3306', cluster: 2, imono: 41.3, cd4: 14.2, cd8: 12.8, plasma: 21.4, nk:  6.2, treg:  4.1 },
      { name: 'GSM3307', cluster: 2, imono: 38.7, cd4: 16.8, cd8: 11.4, plasma: 18.9, nk:  8.3, treg:  5.9 },
      { name: 'GSM3308', cluster: 2, imono: 44.2, cd4: 13.1, cd8: 10.7, plasma: 23.1, nk:  5.4, treg:  3.5 },
      { name: 'GSM3309', cluster: 2, imono: 36.4, cd4: 17.9, cd8: 13.2, plasma: 16.8, nk:  9.8, treg:  5.9 },
      { name: 'GSM3310', cluster: 2, imono: 42.8, cd4: 12.4, cd8: 11.9, plasma: 22.3, nk:  5.8, treg:  4.8 },
      { name: 'GSM3311', cluster: 3, imono: 24.7, cd4: 23.8, cd8: 18.4, plasma: 12.3, nk: 11.9, treg:  8.9 },
      { name: 'GSM3312', cluster: 3, imono: 27.3, cd4: 21.2, cd8: 17.8, plasma: 14.7, nk: 10.4, treg:  8.6 },
      { name: 'GSM3313', cluster: 3, imono: 22.4, cd4: 25.6, cd8: 19.2, plasma: 11.8, nk: 12.8, treg:  8.2 },
      { name: 'GSM3314', cluster: 3, imono: 29.1, cd4: 20.4, cd8: 16.3, plasma: 15.9, nk:  9.7, treg:  8.6 },
      { name: 'GSM3315', cluster: 3, imono: 25.8, cd4: 22.7, cd8: 18.7, plasma: 13.2, nk: 11.3, treg:  8.3 },
    ],
    hmColMax: { imono: 44.2, cd4: 33.4, cd8: 25.6, plasma: 23.1, nk: 17.4, treg: 10.8 },
    hmHeaders: ['I.Mono', 'CD4', 'CD8', 'Plasma', 'NK', 'Treg'],
    clusterLabels: { 1: 'T cell-enriched (mild)', 2: 'Monocyte-enriched (severe)', 3: 'Mixed (moderate)' },
    clusters: [1, 2, 3],
    dominantLabel: '28.4%', dominantSub: 'Inflam. monocytes',
    correlation: '0.891',
    completionMsg: '15 samples · 10 cell types · 4m 12s',
    chromeInfo: '15 samples · 4m 12s',
    qualityMetrics: [{ label: 'Pearson r', value: '0.891' }, { label: 'RMSE', value: '0.043' }, { label: 'R²', value: '0.794' }],
    logLines: [
      '[00:00] Validating input matrix (18,342 genes × 15 samples)...',
      '[00:02] Gene coverage check: 94.7% against PBMC COVID-19 v1.2',
      '[00:04] Normalizing expression values (TMM method)...',
      '[00:08] Running CellReveal deconvolution engine...',
      '[00:13] Processing sample batch 1/3 (GSM3301–GSM3305)...',
      '[00:18] Processing sample batch 2/3 (GSM3306–GSM3310)...',
      '[00:24] Processing sample batch 3/3 (GSM3311–GSM3315)...',
      '[00:28] Computing quality metrics and confidence intervals...',
      '[00:31] Pearson r = 0.891  ·  RMSE = 0.043  ·  R² = 0.794',
      '[00:32] Generating PDF report and export files...',
      '[00:34] Analysis complete. 15 samples processed successfully.',
    ],
    reportItems: [
      { section: '1.', title: 'Data overview', sub: '15 samples · 18,342 genes · PBMC COVID-19 v1.2' },
      { section: '2.', title: 'Quality control', sub: 'Gene coverage 94.7% · Pearson r = 0.891' },
      { section: '3.', title: 'Mean proportions', sub: 'Inflammatory monocytes dominant at 28.4%' },
      { section: '4.', title: 'Per-sample breakdown', sub: 'Table with 15 rows × 10 cell types · 3 clusters' },
      { section: '5.', title: 'Methods', sub: 'CellReveal Engine · PBMC COVID-19 v1.2 · DOI citation' },
    ],
  },

  'pbmc-v3': {
    cellTypes: [
      { name: 'CD4+ helper T',    key: 'cd4',   mean: 0.312, sd: 0.086, color: 'hsl(182, 72%, 28%)' },
      { name: 'CD8+ cytotoxic T', key: 'cd8',   mean: 0.211, sd: 0.058, color: 'hsl(201, 80%, 42%)' },
      { name: 'Monocytes',        key: 'mono',  mean: 0.207, sd: 0.112, color: 'hsl(38, 68%, 48%)' },
      { name: 'NK cells',         key: 'nk',    mean: 0.146, sd: 0.054, color: 'hsl(200, 68%, 56%)' },
      { name: 'B cells',          key: 'bcell', mean: 0.081, sd: 0.009, color: 'hsl(160, 55%, 48%)' },
      { name: 'Dendritic cells',  key: 'dc',    mean: 0.042, sd: 0.011, color: 'hsl(38, 70%, 58%)' },
    ],
    samples: [
      { name: 'GSM001', cluster: 1, cd4: 44.2, mono: 13.4, cd8: 18.1, nk: 12.6, bcell: 8.3, dc: 3.4 },
      { name: 'GSM002', cluster: 1, cd4: 41.8, mono: 15.2, cd8: 19.3, nk: 11.1, bcell: 8.7, dc: 3.9 },
      { name: 'GSM003', cluster: 1, cd4: 43.5, mono: 10.8, cd8: 21.2, nk:  9.4, bcell: 9.8, dc: 5.3 },
      { name: 'GSM004', cluster: 1, cd4: 38.9, mono: 16.1, cd8: 17.8, nk: 14.2, bcell: 9.1, dc: 3.9 },
      { name: 'GSM005', cluster: 1, cd4: 46.1, mono: 12.3, cd8: 20.4, nk: 10.9, bcell: 7.2, dc: 3.1 },
      { name: 'GSM006', cluster: 2, cd4: 24.7, mono: 38.2, cd8: 14.8, nk:  8.3, bcell: 8.6, dc: 5.4 },
      { name: 'GSM007', cluster: 2, cd4: 21.3, mono: 41.5, cd8: 13.2, nk:  9.7, bcell: 6.9, dc: 7.4 },
      { name: 'GSM008', cluster: 2, cd4: 26.8, mono: 33.4, cd8: 16.7, nk: 10.2, bcell: 8.4, dc: 4.5 },
      { name: 'GSM009', cluster: 2, cd4: 22.9, mono: 36.8, cd8: 15.4, nk: 11.1, bcell: 9.2, dc: 4.6 },
      { name: 'GSM010', cluster: 2, cd4: 28.4, mono: 29.7, cd8: 18.1, nk: 13.3, bcell: 7.8, dc: 2.7 },
      { name: 'GSM011', cluster: 3, cd4: 24.3, mono: 11.7, cd8: 30.2, nk: 21.4, bcell: 8.2, dc: 4.2 },
      { name: 'GSM012', cluster: 3, cd4: 27.8, mono: 13.4, cd8: 28.6, nk: 19.7, bcell: 7.1, dc: 3.4 },
      { name: 'GSM013', cluster: 3, cd4: 29.2, mono: 10.1, cd8: 26.4, nk: 22.8, bcell: 6.8, dc: 4.7 },
      { name: 'GSM014', cluster: 3, cd4: 25.6, mono: 15.3, cd8: 25.1, nk: 23.5, bcell: 7.5, dc: 3.0 },
      { name: 'GSM015', cluster: 3, cd4: 23.1, mono: 12.8, cd8: 31.7, nk: 20.3, bcell: 8.4, dc: 3.7 },
    ],
    hmColMax: { cd4: 46.1, mono: 41.5, cd8: 31.7, nk: 23.5, bcell: 9.8, dc: 7.4 },
    hmHeaders: ['CD4', 'CD8', 'Mono', 'NK', 'B', 'DC'],
    clusterLabels: { 1: 'T cell-enriched', 2: 'Monocyte-enriched', 3: 'NK/CD8-enriched' },
    clusters: [1, 2, 3],
    dominantLabel: '31.2%', dominantSub: 'CD4+ helper T',
    correlation: '0.847',
    completionMsg: '15 samples · 8 cell types · 3m 47s',
    chromeInfo: '15 samples · 3m 47s',
    qualityMetrics: [{ label: 'Pearson r', value: '0.847' }, { label: 'RMSE', value: '0.061' }, { label: 'R²', value: '0.717' }],
    logLines: [
      '[00:00] Validating input matrix (18,342 genes × 15 samples)...',
      '[00:02] Gene coverage check: 97.2% against PBMC Immune v3.1',
      '[00:04] Normalizing expression values (TMM method)...',
      '[00:08] Running CellReveal deconvolution engine...',
      '[00:13] Processing sample batch 1/3 (GSM001–GSM005)...',
      '[00:18] Processing sample batch 2/3 (GSM006–GSM010)...',
      '[00:24] Processing sample batch 3/3 (GSM011–GSM015)...',
      '[00:28] Computing quality metrics and confidence intervals...',
      '[00:31] Pearson r = 0.847  ·  RMSE = 0.061  ·  R² = 0.717',
      '[00:32] Generating PDF report and export files...',
      '[00:34] Analysis complete. 15 samples processed successfully.',
    ],
    reportItems: [
      { section: '1.', title: 'Data overview', sub: '15 samples · 18,342 genes · PBMC Immune v3.1' },
      { section: '2.', title: 'Quality control', sub: 'Gene coverage 97.2% · Pearson r = 0.847' },
      { section: '3.', title: 'Mean proportions', sub: 'CD4+ helper T dominant at 31.2%' },
      { section: '4.', title: 'Per-sample breakdown', sub: 'Table with 15 rows × 8 cell types · 3 clusters' },
      { section: '5.', title: 'Methods', sub: 'CellReveal Engine · PBMC Immune v3.1 · DOI citation' },
    ],
  },

  'lung-tme': {
    cellTypes: [
      { name: 'TAMs (M2-like)',      key: 'tam',   mean: 0.243, sd: 0.078, color: 'hsl(38, 68%, 48%)' },
      { name: 'Tumor epithelial',    key: 'tumor', mean: 0.224, sd: 0.121, color: 'hsl(4, 62%, 48%)' },
      { name: 'CD8+ T (exhausted)', key: 'cd8',   mean: 0.187, sd: 0.064, color: 'hsl(201, 80%, 42%)' },
      { name: 'CAFs',               key: 'caf',   mean: 0.142, sd: 0.056, color: 'hsl(25, 58%, 56%)' },
      { name: 'CD4+ T',             key: 'cd4',   mean: 0.118, sd: 0.043, color: 'hsl(182, 72%, 28%)' },
      { name: 'MDSCs',              key: 'mdsc',  mean: 0.086, sd: 0.032, color: 'hsl(220, 55%, 55%)' },
    ],
    samples: [
      { name: 'GSM4801', cluster: 1, tam: 11.2, tumor: 38.4, cd8:  8.3, caf: 28.7, cd4:  8.2, mdsc: 5.2 },
      { name: 'GSM4802', cluster: 1, tam:  9.8, tumor: 41.2, cd8:  7.4, caf: 31.3, cd4:  6.4, mdsc: 3.9 },
      { name: 'GSM4803', cluster: 1, tam: 12.7, tumor: 36.8, cd8:  9.1, caf: 27.4, cd4:  9.3, mdsc: 4.7 },
      { name: 'GSM4804', cluster: 1, tam: 10.4, tumor: 39.7, cd8:  8.7, caf: 29.8, cd4:  7.3, mdsc: 4.1 },
      { name: 'GSM4805', cluster: 1, tam: 11.8, tumor: 37.3, cd8:  7.8, caf: 30.2, cd4:  8.1, mdsc: 4.8 },
      { name: 'GSM4806', cluster: 1, tam: 13.2, tumor: 35.4, cd8:  9.8, caf: 26.9, cd4:  9.7, mdsc: 5.0 },
      { name: 'GSM4807', cluster: 2, tam: 31.4, tumor: 14.8, cd8: 28.7, caf:  9.3, cd4: 11.4, mdsc: 4.4 },
      { name: 'GSM4808', cluster: 2, tam: 28.7, tumor: 16.2, cd8: 31.4, caf:  8.7, cd4: 10.2, mdsc: 4.8 },
      { name: 'GSM4809', cluster: 2, tam: 33.2, tumor: 13.4, cd8: 27.8, caf: 10.1, cd4: 12.3, mdsc: 3.2 },
      { name: 'GSM4810', cluster: 2, tam: 29.8, tumor: 15.7, cd8: 29.4, caf:  9.8, cd4: 11.7, mdsc: 3.6 },
      { name: 'GSM4811', cluster: 2, tam: 30.4, tumor: 14.3, cd8: 30.1, caf:  8.4, cd4: 12.8, mdsc: 4.0 },
      { name: 'GSM4812', cluster: 2, tam: 27.3, tumor: 17.8, cd8: 32.4, caf:  7.8, cd4: 10.4, mdsc: 4.3 },
      { name: 'GSM4813', cluster: 3, tam: 22.4, tumor: 23.7, cd8: 18.4, caf: 17.3, cd4: 12.4, mdsc: 5.8 },
      { name: 'GSM4814', cluster: 3, tam: 24.8, tumor: 21.3, cd8: 19.7, caf: 16.8, cd4: 11.7, mdsc: 5.7 },
      { name: 'GSM4815', cluster: 3, tam: 23.1, tumor: 22.8, cd8: 18.9, caf: 17.9, cd4: 12.1, mdsc: 5.2 },
    ],
    hmColMax: { tam: 33.2, tumor: 41.2, cd8: 32.4, caf: 31.3, cd4: 12.8, mdsc: 5.8 },
    hmHeaders: ['TAMs', 'Tumor', 'CD8', 'CAFs', 'CD4', 'MDSC'],
    clusterLabels: { 1: 'Cold (CAF/stromal)', 2: 'Hot (immune-rich)', 3: 'Mixed TME' },
    clusters: [1, 2, 3],
    dominantLabel: '24.3%', dominantSub: 'TAMs (M2-like)',
    correlation: '0.874',
    completionMsg: '28 samples · 14 cell types · 7m 23s',
    chromeInfo: '28 samples · 7m 23s',
    qualityMetrics: [{ label: 'Pearson r', value: '0.874' }, { label: 'RMSE', value: '0.051' }, { label: 'R²', value: '0.764' }],
    logLines: [
      '[00:00] Validating input matrix (31,204 genes × 28 samples)...',
      '[00:02] Gene coverage check: 91.3% against LungTME — NSCLC v2.0',
      '[00:04] Normalizing expression values (TMM method)...',
      '[00:09] Running CellReveal deconvolution engine...',
      '[00:16] Processing sample batch 1/4 (GSM4801–GSM4807)...',
      '[00:23] Processing sample batch 2/4 (GSM4808–GSM4814)...',
      '[00:31] Processing sample batch 3/4 (GSM4815–GSM4821)...',
      '[00:38] Processing sample batch 4/4 (GSM4822–GSM4828)...',
      '[00:44] Computing quality metrics and confidence intervals...',
      '[00:47] Pearson r = 0.874  ·  RMSE = 0.051  ·  R² = 0.764',
      '[00:50] Analysis complete. 28 samples processed successfully.',
    ],
    reportItems: [
      { section: '1.', title: 'Data overview', sub: '28 samples · 31,204 genes · LungTME — NSCLC v2.0' },
      { section: '2.', title: 'Quality control', sub: 'Gene coverage 91.3% · Pearson r = 0.874' },
      { section: '3.', title: 'Mean proportions', sub: 'TAMs (M2-like) dominant at 24.3%' },
      { section: '4.', title: 'Per-sample breakdown', sub: 'Table with 28 rows × 14 cell types · 3 clusters' },
      { section: '5.', title: 'Methods', sub: 'CellReveal Engine · LungTME — NSCLC v2.0 · DOI citation' },
    ],
  },

  'brain-atlas': {
    cellTypes: [
      { name: 'Microglia / TAMs',    key: 'micro', mean: 0.382, sd: 0.094, color: 'hsl(38, 68%, 48%)' },
      { name: 'Astrocytes',          key: 'astro', mean: 0.227, sd: 0.073, color: 'hsl(160, 55%, 42%)' },
      { name: 'Oligodendrocytes',    key: 'oligo', mean: 0.164, sd: 0.058, color: 'hsl(200, 60%, 55%)' },
      { name: 'CD8+ T (exhausted)', key: 'cd8',   mean: 0.124, sd: 0.061, color: 'hsl(201, 80%, 42%)' },
      { name: 'Endothelial',         key: 'endo',  mean: 0.066, sd: 0.028, color: 'hsl(182, 72%, 28%)' },
      { name: 'Tregs',               key: 'treg',  mean: 0.037, sd: 0.018, color: 'hsl(182, 55%, 52%)' },
    ],
    samples: [
      { name: 'GSM7201', cluster: 1, micro: 48.3, astro: 18.4, oligo: 11.2, cd8: 14.7, endo:  5.2, treg: 2.2 },
      { name: 'GSM7202', cluster: 1, micro: 44.7, astro: 20.1, oligo: 12.8, cd8: 13.4, endo:  6.3, treg: 2.7 },
      { name: 'GSM7203', cluster: 1, micro: 51.2, astro: 17.3, oligo: 10.4, cd8: 15.8, endo:  3.8, treg: 1.5 },
      { name: 'GSM7204', cluster: 1, micro: 46.8, astro: 19.7, oligo: 11.7, cd8: 12.9, endo:  5.7, treg: 3.2 },
      { name: 'GSM7205', cluster: 1, micro: 43.4, astro: 21.3, oligo: 13.1, cd8: 13.7, endo:  6.1, treg: 2.4 },
      { name: 'GSM7206', cluster: 2, micro: 22.4, astro: 28.7, oligo: 24.3, cd8:  8.4, endo: 10.3, treg: 5.9 },
      { name: 'GSM7207', cluster: 2, micro: 19.8, astro: 31.2, oligo: 26.7, cd8:  7.2, endo:  9.8, treg: 5.3 },
      { name: 'GSM7208', cluster: 2, micro: 24.1, astro: 27.4, oligo: 22.8, cd8:  9.7, endo: 10.7, treg: 5.3 },
      { name: 'GSM7209', cluster: 2, micro: 21.3, astro: 29.8, oligo: 25.4, cd8:  8.1, endo:  9.7, treg: 5.7 },
    ],
    hmColMax: { micro: 51.2, astro: 31.2, oligo: 26.7, cd8: 15.8, endo: 10.7, treg: 5.9 },
    hmHeaders: ['Micro', 'Astro', 'Oligo', 'CD8', 'Endo', 'Treg'],
    clusterLabels: { 1: 'Immune-high GBM', 2: 'Immune-low GBM' },
    clusters: [1, 2],
    dominantLabel: '38.2%', dominantSub: 'Microglia / TAMs',
    correlation: '0.863',
    completionMsg: '9 samples · 11 cell types · 2m 31s',
    chromeInfo: '9 samples · 2m 31s',
    qualityMetrics: [{ label: 'Pearson r', value: '0.863' }, { label: 'RMSE', value: '0.058' }, { label: 'R²', value: '0.745' }],
    logLines: [
      '[00:00] Validating input matrix (22,417 genes × 9 samples)...',
      '[00:02] Gene coverage check: 88.4% against BrainCell Atlas v2.0',
      '[00:04] Normalizing expression values (quantile method)...',
      '[00:08] Running CellReveal deconvolution engine...',
      '[00:13] Processing sample batch 1/2 (GSM7201–GSM7205)...',
      '[00:18] Processing sample batch 2/2 (GSM7206–GSM7209)...',
      '[00:22] Computing quality metrics and confidence intervals...',
      '[00:25] Pearson r = 0.863  ·  RMSE = 0.058  ·  R² = 0.745',
      '[00:26] Generating PDF report and export files...',
      '[00:28] Analysis complete. 9 samples processed successfully.',
    ],
    reportItems: [
      { section: '1.', title: 'Data overview', sub: '9 samples · 22,417 genes · BrainCell Atlas v2.0' },
      { section: '2.', title: 'Quality control', sub: 'Gene coverage 88.4% · Pearson r = 0.863' },
      { section: '3.', title: 'Mean proportions', sub: 'Microglia / TAMs dominant at 38.2%' },
      { section: '4.', title: 'Per-sample breakdown', sub: 'Table with 9 rows × 11 cell types · 2 clusters' },
      { section: '5.', title: 'Methods', sub: 'CellReveal Engine · BrainCell Atlas v2.0 · DOI citation' },
    ],
  },

  'liver-hcc': {
    cellTypes: [
      { name: 'HCC tumor cells',    key: 'hcc',     mean: 0.314, sd: 0.123, color: 'hsl(4, 62%, 48%)' },
      { name: 'Kupffer / TAMs',     key: 'kupffer', mean: 0.228, sd: 0.087, color: 'hsl(38, 68%, 48%)' },
      { name: 'CD8+ TILs',         key: 'cd8',     mean: 0.173, sd: 0.062, color: 'hsl(201, 80%, 42%)' },
      { name: 'Stellate cells',     key: 'stell',   mean: 0.146, sd: 0.054, color: 'hsl(25, 58%, 56%)' },
      { name: 'Tregs',             key: 'treg',    mean: 0.089, sd: 0.038, color: 'hsl(182, 55%, 52%)' },
      { name: 'LSECs',             key: 'lsec',    mean: 0.050, sd: 0.021, color: 'hsl(182, 72%, 28%)' },
    ],
    samples: [
      { name: 'GSM8101', cluster: 1, hcc: 47.3, kupffer: 18.4, cd8:  9.2, stell: 18.7, treg: 5.2, lsec: 1.2 },
      { name: 'GSM8102', cluster: 1, hcc: 43.7, kupffer: 20.2, cd8: 10.8, stell: 17.3, treg: 6.4, lsec: 1.6 },
      { name: 'GSM8103', cluster: 1, hcc: 51.2, kupffer: 16.3, cd8:  8.4, stell: 16.8, treg: 5.8, lsec: 1.5 },
      { name: 'GSM8104', cluster: 1, hcc: 45.8, kupffer: 19.7, cd8:  9.7, stell: 18.1, treg: 5.3, lsec: 1.4 },
      { name: 'GSM8105', cluster: 1, hcc: 48.4, kupffer: 17.8, cd8:  8.9, stell: 17.4, treg: 5.9, lsec: 1.6 },
      { name: 'GSM8106', cluster: 2, hcc: 17.4, kupffer: 34.2, cd8: 28.7, stell: 11.3, treg: 6.4, lsec: 2.0 },
      { name: 'GSM8107', cluster: 2, hcc: 14.8, kupffer: 36.7, cd8: 31.4, stell:  9.7, treg: 5.8, lsec: 1.6 },
      { name: 'GSM8108', cluster: 2, hcc: 19.2, kupffer: 31.8, cd8: 27.3, stell: 12.4, treg: 7.2, lsec: 2.1 },
      { name: 'GSM8109', cluster: 2, hcc: 16.3, kupffer: 33.4, cd8: 29.8, stell: 10.8, treg: 6.7, lsec: 3.0 },
      { name: 'GSM8110', cluster: 2, hcc: 15.7, kupffer: 35.2, cd8: 30.4, stell: 10.2, treg: 6.3, lsec: 2.2 },
      { name: 'GSM8111', cluster: 3, hcc: 29.4, kupffer: 24.7, cd8: 18.3, stell: 16.2, treg: 9.4, lsec: 2.0 },
      { name: 'GSM8112', cluster: 3, hcc: 32.7, kupffer: 22.3, cd8: 16.8, stell: 17.4, treg: 8.7, lsec: 2.1 },
      { name: 'GSM8113', cluster: 3, hcc: 28.1, kupffer: 25.8, cd8: 19.7, stell: 15.3, treg: 9.1, lsec: 2.0 },
      { name: 'GSM8114', cluster: 3, hcc: 31.3, kupffer: 23.4, cd8: 17.4, stell: 16.8, treg: 9.2, lsec: 1.9 },
      { name: 'GSM8115', cluster: 3, hcc: 27.8, kupffer: 26.3, cd8: 20.1, stell: 14.7, treg: 8.9, lsec: 2.2 },
    ],
    hmColMax: { hcc: 51.2, kupffer: 36.7, cd8: 31.4, stell: 18.7, treg: 9.4, lsec: 3.0 },
    hmHeaders: ['HCC', 'Kupffer', 'CD8', 'Stellate', 'Treg', 'LSEC'],
    clusterLabels: { 1: 'Cold (tumor-rich)', 2: 'Hot (immune-rich)', 3: 'Mixed' },
    clusters: [1, 2, 3],
    dominantLabel: '31.4%', dominantSub: 'HCC tumor cells',
    correlation: '0.817',
    completionMsg: '15 samples · 9 cell types · 5m 08s',
    chromeInfo: '15 samples · 5m 08s',
    qualityMetrics: [{ label: 'Pearson r', value: '0.817' }, { label: 'RMSE', value: '0.074' }, { label: 'R²', value: '0.668' }],
    logLines: [
      '[00:00] Validating input matrix (26,731 genes × 15 samples)...',
      '[00:02] Gene coverage check: 89.1% against LiverCell — HCC v1.3',
      '[00:04] Normalizing expression values (TMM method)...',
      '[00:08] Running CellReveal deconvolution engine...',
      '[00:13] Processing sample batch 1/3 (GSM8101–GSM8105)...',
      '[00:18] Processing sample batch 2/3 (GSM8106–GSM8110)...',
      '[00:24] Processing sample batch 3/3 (GSM8111–GSM8115)...',
      '[00:28] Computing quality metrics and confidence intervals...',
      '[00:31] Pearson r = 0.817  ·  RMSE = 0.074  ·  R² = 0.668',
      '[00:32] Generating PDF report and export files...',
      '[00:34] Analysis complete. 15 samples processed successfully.',
    ],
    reportItems: [
      { section: '1.', title: 'Data overview', sub: '15 samples · 26,731 genes · LiverCell — HCC v1.3' },
      { section: '2.', title: 'Quality control', sub: 'Gene coverage 89.1% · Pearson r = 0.817' },
      { section: '3.', title: 'Mean proportions', sub: 'HCC tumor cells dominant at 31.4%' },
      { section: '4.', title: 'Per-sample breakdown', sub: 'Table with 15 rows × 9 cell types · 3 clusters' },
      { section: '5.', title: 'Methods', sub: 'CellReveal Engine · LiverCell — HCC v1.3 · DOI citation' },
    ],
  },
};

// ─── filters ──────────────────────────────────────────────────────────────────

const SIG_FILTERS = ['All', 'Blood', 'Lung', 'Brain', 'Liver', 'Tumor'] as const;
type SigFilter = typeof SIG_FILTERS[number];

function matchesSigFilter(sig: MockSignature, f: SigFilter) {
  if (f === 'All') return true;
  if (f === 'Blood') return sig.tissueKey === 'blood';
  if (f === 'Lung') return sig.tissueKey === 'lung';
  if (f === 'Brain') return sig.tissueKey === 'brain';
  if (f === 'Liver') return sig.tissueKey === 'liver';
  if (f === 'Tumor') return ['lung', 'liver', 'breast', 'skin', 'pancreas'].includes(sig.tissueKey);
  return true;
}

function hmBg(v: number, colMax: number): string {
  const t = Math.min(v / colMax, 1);
  return `hsl(182, ${Math.round(t * 72)}%, ${Math.round(97 - t * 70)}%)`;
}

function hmText(v: number, colMax: number): string {
  const t = Math.min(v / colMax, 1);
  return t > 0.55 ? 'rgba(255,255,255,0.92)' : 'rgba(15,118,110,0.75)';
}

const WORKFLOW_STEPS = [
  { step: '01', title: 'Choose a dataset', icon: Database },
  { step: '02', title: 'Select Signature', icon: Stack },
  { step: '03', title: 'Run Analysis', icon: Lightning },
  { step: '04', title: 'Explore Results', icon: ChartLineUp },
];

// ─── component ────────────────────────────────────────────────────────────────

export function Demo() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [selectedSigId, setSelectedSigId] = useState<string>('');
  const [sigFilter, setSigFilter] = useState<SigFilter>('All');
  const [analysisState, setAnalysisState] = useState<'idle' | 'running' | 'done'>('idle');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [resultsTab, setResultsTab] = useState<'summary' | 'samples' | 'visualizations' | 'report'>('summary');
  const logRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentStep === 3) {
      setAnalysisState('idle');
      setAnalysisProgress(0);
      setLogLines([]);
    }
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentStep]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLines]);

  const canContinue = () => {
    if (currentStep === 1) return selectedDataset !== null;
    if (currentStep === 2) return selectedSigId !== '';
    if (currentStep === 3) return analysisState === 'done';
    return false;
  };

  const handleRunAnalysis = () => {
    setAnalysisState('running');
    setAnalysisProgress(0);
    setLogLines([]);

    const lines = SIGNATURE_RESULTS[selectedSigId]?.logLines ?? SIGNATURE_RESULTS['pbmc-covid'].logLines;
    const totalMs = 3800;
    const start = Date.now();

    const tick = setInterval(() => {
      const pct = Math.min(((Date.now() - start) / totalMs) * 100, 100);
      setAnalysisProgress(pct);
      if (pct >= 100) { clearInterval(tick); setAnalysisState('done'); }
    }, 40);

    lines.forEach((line, i) => {
      setTimeout(() => setLogLines(prev => [...prev, line]), (i * totalMs) / lines.length);
    });
  };

  const selectedSig = mockSignatures.find(s => s.id === selectedSigId);
  const selectedDs = mockDatasets.find(d => d.id === selectedDataset);
  const recommendedSigId = RECOMMENDED_SIGS[selectedDataset ?? ''] ?? '';
  const activeResults = SIGNATURE_RESULTS[selectedSigId] ?? SIGNATURE_RESULTS['pbmc-covid'];

  return (
    <div className="min-h-screen pt-16 bg-slate-50">

      {/* ── Demo banner ───────────────────────────────────────────────────── */}
      <div className="bg-teal-50 border-b border-teal-200/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Info className="w-4 h-4 text-teal-600 shrink-0" weight="bold" />
            <p className="text-sm text-teal-800">
              <span className="font-semibold">Interactive walkthrough</span>
              <span className="text-teal-700/70 hidden sm:inline"> — click through each step to see how CellReveal works. No real data is processed.</span>
            </p>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:text-teal-800 transition-colors shrink-0">
            <ArrowLeft className="w-3.5 h-3.5" weight="bold" />
            <span className="hidden sm:inline">Back to home</span>
            <span className="sm:hidden">Home</span>
          </Link>
        </div>
      </div>

      {/* ── Step progress bar ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-0">
            {WORKFLOW_STEPS.map((ws, i) => {
              const num = i + 1;
              const isDone = num < currentStep;
              const isActive = num === currentStep;
              return (
                <div key={ws.step} className="flex items-center flex-1 last:flex-none">
                  <button
                    onClick={() => isDone ? setCurrentStep(num) : undefined}
                    disabled={!isDone && !isActive}
                    className={`flex items-center gap-2.5 ${isDone ? 'cursor-pointer group' : 'cursor-default'}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                      isDone ? 'bg-teal-600 text-white group-hover:bg-teal-700'
                        : isActive ? 'bg-teal-600 text-white ring-4 ring-teal-100'
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isDone ? <CheckCircle className="w-4 h-4" weight="bold" /> : ws.step}
                    </div>
                    <div className="hidden sm:block">
                      <p className={`text-xs font-mono uppercase tracking-[0.2em] leading-none mb-0.5 ${
                        isActive ? 'text-teal-400' : isDone ? 'text-teal-500/60' : 'text-slate-300'
                      }`}>{ws.step}</p>
                      <p className={`text-sm font-medium leading-none ${
                        isActive ? 'text-teal-700' : isDone ? 'text-teal-600' : 'text-slate-400'
                      }`}>{ws.title}</p>
                    </div>
                  </button>
                  {i < WORKFLOW_STEPS.length - 1 && (
                    <div className={`flex-1 mx-3 h-px transition-colors duration-300 ${num < currentStep ? 'bg-teal-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div ref={contentRef} className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Choose dataset ───────────────────────────────────────── */}
          {currentStep === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
              <StepHeader step="01" title="Choose a dataset"
                desc="Select one of these curated public datasets to see how CellReveal processes bulk gene expression data. Each represents a distinct biological question." />

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {mockDatasets.map((ds, i) => {
                  const isSelected = selectedDataset === ds.id;
                  return (
                    <motion.button key={ds.id}
                      onClick={() => { setSelectedDataset(ds.id); setSelectedSigId(''); }}
                      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.28, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                      whileTap={{ scale: 0.985 }}
                      className={`relative text-left rounded-2xl border-2 p-6 transition-all duration-200 flex flex-col gap-4 group ${
                        isSelected ? 'border-teal-400 bg-white shadow-[0_0_0_4px_rgba(20,184,166,0.1)]'
                          : 'border-slate-200 bg-white hover:border-teal-300/70 hover:shadow-xs'
                      }`}>
                      {isSelected && <div className="absolute top-4 right-4"><CheckCircle className="w-5 h-5 text-teal-500" weight="fill" /></div>}
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                          isSelected ? 'bg-teal-100 border border-teal-200' : 'bg-slate-100 border border-slate-200 group-hover:bg-teal-50 group-hover:border-teal-100'
                        }`}>
                          <Database className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-slate-500 group-hover:text-teal-500'}`} weight="bold" />
                        </div>
                        <div>
                          <span className={`font-mono text-[11px] tracking-wider font-medium ${isSelected ? 'text-teal-600' : 'text-slate-500'}`}>{ds.geoId}</span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
                              isSelected ? 'bg-teal-50 text-teal-600/80 border-teal-100' : 'bg-slate-50 text-slate-600 border-slate-200'
                            }`}>{ds.platform}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-sm leading-snug mb-2 ${isSelected ? 'text-teal-900' : 'text-foreground'}`}>{ds.title}</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">{ds.description}</p>
                      </div>
                      <div className={`flex items-center gap-3 pt-3 border-t text-xs font-mono tabular-nums ${
                        isSelected ? 'border-teal-100 text-teal-700/60' : 'border-slate-100 text-slate-500'
                      }`}>
                        <span>{ds.samples} samples</span><span className="text-[9px]">·</span>
                        <span>{ds.genes.toLocaleString()} genes</span><span className="text-[9px]">·</span>
                        <span>{ds.size}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Select Signature ─────────────────────────────────────── */}
          {currentStep === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
              <StepHeader step="02" title="Select a cell-type signature"
                desc="Choose the reference that matches your tissue, disease context, and organism. The best match for your dataset is highlighted — you can also browse all available signatures." />

              <div className="mt-8 flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground/40 mr-1">Filter</span>
                {SIG_FILTERS.map((f) => (
                  <button key={f} onClick={() => setSigFilter(f)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 active:scale-95 ${
                      sigFilter === f ? 'bg-teal-100 border border-teal-300/60 text-teal-700'
                        : 'border border-slate-200 text-muted-foreground/55 hover:text-muted-foreground hover:border-slate-300 bg-white'
                    }`}>{f}</button>
                ))}
                {selectedSigId && (
                  <span className="ml-auto text-xs text-teal-600 font-medium">
                    {mockSignatures.find(s => s.id === selectedSigId)?.name} selected
                  </span>
                )}
              </div>

              <div className="mt-5">
                <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-muted-foreground/40 mb-4">
                  {mockSignatures.filter(s => matchesSigFilter(s, sigFilter)).length} signatures available — click a card to select
                </p>
                <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" layout>
                  <AnimatePresence>
                    {mockSignatures
                      .filter(s => matchesSigFilter(s, sigFilter))
                      .sort((a, b) => a.id === recommendedSigId ? -1 : b.id === recommendedSigId ? 1 : 0)
                      .map((sig, i) => {
                        const isSelected = selectedSigId === sig.id;
                        const isRecommended = sig.id === recommendedSigId;
                        const imgUrl = sig.imageUrl ?? getTissueImage(sig.tissueKey);
                        return (
                          <motion.div key={sig.id} layout
                            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.92 }}
                            transition={{ duration: 0.22, delay: i * 0.035, ease: [0.16, 1, 0.3, 1] }}
                            onClick={() => setSelectedSigId(prev => prev === sig.id ? '' : sig.id)}
                            className="relative cursor-pointer group h-64" style={{ perspective: '1200px' }}>
                            <motion.div
                              className="absolute -inset-0.5 rounded-xl blur-sm pointer-events-none bg-teal-500"
                              animate={{ opacity: isSelected ? 0.38 : 0 }}
                              transition={{ duration: 0.2 }} />
                            <motion.div
                              animate={{ rotateY: isSelected ? 180 : 0 }}
                              transition={{ rotateY: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }}
                              whileHover={{ scale: isSelected ? 1 : 1.015 }} whileTap={{ scale: 0.98 }}
                              className={`relative w-full h-full rounded-xl border-2 transition-colors duration-200 ${
                                isSelected ? 'border-teal-500'
                                  : isRecommended ? 'border-teal-300 hover:border-teal-400'
                                  : 'border-slate-200 hover:border-teal-300/60'
                              }`}
                              style={{ transformStyle: 'preserve-3d', WebkitTransformStyle: 'preserve-3d' } as React.CSSProperties}>
                              {/* Front */}
                              <div className="absolute inset-0 rounded-xl overflow-hidden bg-white"
                                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' } as React.CSSProperties}>
                                {isRecommended && (
                                  <div className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors duration-300 ${
                                    isSelected ? 'bg-teal-600' : 'bg-teal-50 border-b border-teal-200'
                                  }`}>
                                    <Sparkle className={`w-3 h-3 shrink-0 ${isSelected ? 'text-teal-200' : 'text-teal-500'}`} weight="fill" />
                                    <span className={`text-[10px] font-semibold tracking-wide ${isSelected ? 'text-white' : 'text-teal-700'}`}>
                                      Best match for your dataset
                                    </span>
                                    {isSelected
                                      ? <CheckCircle className="w-3.5 h-3.5 text-teal-200 ml-auto shrink-0" weight="fill" />
                                      : <span className="ml-auto shrink-0 text-[10px] text-teal-500 font-medium">click to select</span>
                                    }
                                  </div>
                                )}
                                <div className={`relative overflow-hidden ${isRecommended ? 'h-32' : 'h-36'}`}>
                                  <img src={imgUrl} alt={sig.tissue} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                  <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
                                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-600/85 text-white backdrop-blur-sm">{sig.tissue}</div>
                                  {!isRecommended && (
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/30 text-white backdrop-blur-sm">click to select</span>
                                    </div>
                                  )}
                                </div>
                                <div className={`p-3.5 flex flex-col justify-between bg-white ${
                                  isRecommended ? 'h-[calc(100%-8rem-1.75rem)]' : 'h-[calc(100%-9rem)]'
                                }`}>
                                  <div>
                                    <div className="flex items-start justify-between gap-1.5 mb-1">
                                      <h4 className="font-semibold text-sm leading-snug text-foreground">{sig.name}</h4>
                                      <span className="shrink-0 font-mono text-[9px] tracking-wide px-1.5 py-0.5 rounded border mt-0.5 text-muted-foreground/55 border-border bg-muted/50">{sig.version}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <span className="font-mono tabular-nums">{sig.cellTypes} cell types</span>
                                    </div>
                                  </div>
                                  {isRecommended && RECOMMENDED_REASON[sig.id] && (
                                    <p className="text-[10px] text-teal-600 font-medium leading-snug">{RECOMMENDED_REASON[sig.id]}</p>
                                  )}
                                </div>
                              </div>
                              {/* Back */}
                              <div className="absolute inset-0 rounded-xl overflow-y-auto p-4 bg-teal-50"
                                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' } as React.CSSProperties}>
                                <div className="flex items-center justify-between mb-3">
                                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-teal-700/50">Cell type composition</p>
                                  <CheckCircle className="w-4 h-4 text-teal-500" weight="fill" />
                                </div>
                                <div className="space-y-2">
                                  {sig.hierarchy.map((group) => (
                                    <div key={group.category}>
                                      <p className="text-[10px] font-semibold leading-none mb-1 text-teal-700">{group.category}</p>
                                      <div className="pl-2.5 border-l-2 border-teal-200 ml-0.5 mb-0.5 space-y-0.5">
                                        {group.types.map((type) => (
                                          <p key={type} className="text-[10px] leading-snug text-foreground/65">{type}</p>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          </motion.div>
                        );
                      })}
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Run Analysis ─────────────────────────────────────────── */}
          {currentStep === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
              <StepHeader step="03" title="Review & run"
                desc="CellReveal runs the deconvolution automatically using a validated engine — no algorithm selection, no environment setup. Review your choices and start the analysis." />

              <div className="mt-8 grid md:grid-cols-[1fr_1.4fr] gap-6">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-5">
                    <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-muted-foreground/40 mb-4">Dataset</p>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                        <Database className="w-4 h-4 text-slate-400" weight="bold" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-[11px] tracking-wide text-muted-foreground/50">{selectedDs?.geoId}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{selectedDs?.platform}</span>
                        </div>
                        <p className="text-sm font-semibold text-foreground leading-snug mb-1.5">{selectedDs?.title}</p>
                        <div className="flex items-center gap-2 text-xs font-mono tabular-nums text-muted-foreground/60">
                          <span>{selectedDs?.samples} samples</span><span className="text-[9px]">·</span>
                          <span>{selectedDs?.genes.toLocaleString()} genes</span><span className="text-[9px]">·</span>
                          <span>{selectedDs?.size}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-xl border bg-white p-5 ${selectedSig?.id === recommendedSigId ? 'border-teal-300/70 bg-teal-50/30' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-muted-foreground/40">Signature</p>
                      {selectedSig?.id === recommendedSigId && (
                        <div className="flex items-center gap-1 text-[10px] font-semibold text-teal-600">
                          <Sparkle className="w-3 h-3" weight="fill" />Best match
                        </div>
                      )}
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Stack className="w-4 h-4 text-teal-600" weight="bold" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-teal-800">{selectedSig?.name}</span>
                          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded border border-teal-200 text-teal-600/70 bg-teal-50">{selectedSig?.version}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{selectedSig?.cellTypes} cell types · {selectedSig?.tissue} · {selectedSig?.organism}</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedSig?.hierarchy.map((group) => (
                            <span key={group.category} className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 border border-teal-100 text-teal-700/70 font-medium">{group.category}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                      <Lightning className="w-4 h-4 text-teal-400" weight="fill" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">CellReveal deconvolution engine</p>
                      <p className="text-xs text-muted-foreground/60 mt-0.5">Validated · no configuration required</p>
                    </div>
                  </div>

                  {analysisState === 'idle' && (
                    <motion.button onClick={handleRunAnalysis} whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.01 }}
                      className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors shadow-[0_8px_24px_-8px_rgba(20,184,166,0.45)]">
                      <Play className="w-4 h-4" weight="fill" />Run Analysis
                    </motion.button>
                  )}
                  {analysisState === 'running' && (
                    <div className="flex items-center gap-2.5 px-5 py-3.5 bg-teal-50 border border-teal-200 rounded-xl">
                      <CircleNotch className="w-4 h-4 text-teal-600 animate-spin shrink-0" weight="bold" />
                      <span className="text-sm text-teal-700 font-medium">Processing…</span>
                    </div>
                  )}
                  {analysisState === 'done' && (
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2.5 px-5 py-3.5 bg-teal-50 border border-teal-300 rounded-xl">
                      <SealCheck className="w-5 h-5 text-teal-600 shrink-0" weight="fill" />
                      <div>
                        <p className="text-sm font-semibold text-teal-800">Analysis complete</p>
                        <p className="text-xs text-teal-600/70">{activeResults.completionMsg}</p>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Terminal */}
                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <div className="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400/50" />
                        <div className="w-2.5 h-2.5 rounded-full bg-teal-400/50" />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground/40 ml-1">analysis.log</span>
                    </div>
                    {analysisState !== 'idle' && (
                      <span className={`font-mono text-[10px] tracking-wide px-2 py-0.5 rounded-full border ${
                        analysisState === 'running' ? 'border-teal-300/60 text-teal-600 bg-teal-50' : 'border-teal-400/60 text-teal-700 bg-teal-50'
                      }`}>{analysisState === 'running' ? 'RUNNING' : 'DONE'}</span>
                    )}
                  </div>
                  {analysisState !== 'idle' && (
                    <div className="h-1 bg-slate-100">
                      <motion.div className="h-full bg-teal-500" animate={{ width: `${analysisProgress}%` }} transition={{ duration: 0.1 }} />
                    </div>
                  )}
                  <div ref={logRef} className="h-64 overflow-y-auto p-4 font-mono text-xs leading-relaxed bg-slate-900">
                    {analysisState === 'idle' ? (
                      <p className="text-slate-500">Waiting for analysis to start…</p>
                    ) : (
                      <>
                        {logLines.map((line, i) => (
                          <motion.p key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}
                            className={`${line.includes('complete') ? 'text-teal-400' : line.includes('Pearson') ? 'text-teal-300' : 'text-slate-400'}`}>
                            {line}
                          </motion.p>
                        ))}
                        {analysisState === 'running' && <span className="text-slate-500 animate-pulse">_</span>}
                      </>
                    )}
                  </div>
                  {analysisState === 'done' && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                      className="border-t border-slate-100 p-4 grid grid-cols-3 gap-3">
                      {activeResults.qualityMetrics.map(m => (
                        <div key={m.label} className="text-center">
                          <p className="text-[10px] font-mono text-muted-foreground/40 uppercase tracking-wider mb-0.5">{m.label}</p>
                          <p className="text-lg font-bold tabular-nums text-teal-600">{m.value}</p>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* ── STEP 4: Results ──────────────────────────────────────────────── */}
          {currentStep === 4 && (
            <motion.div key="step4" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
              <StepHeader step="04" title="Explore your results"
                desc="Interactive charts, per-sample breakdowns, a proportion heatmap, and a formatted PDF report — all generated automatically and ready for publication or review." />

              <div className="mt-8 rounded-xl overflow-hidden border border-slate-200 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.07)]">
                {/* Chrome */}
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-4">
                  <div className="flex gap-1.5 shrink-0">
                    <div className="w-3 h-3 rounded-full bg-red-400/50" />
                    <div className="w-3 h-3 rounded-full bg-amber-400/50" />
                    <div className="w-3 h-3 rounded-full bg-teal-400/50" />
                  </div>
                  <div className="flex-1 text-center">
                    <span className="text-xs font-mono text-muted-foreground/40">CellReveal — Analysis results</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-mono shrink-0 text-muted-foreground/35">
                    <span>{activeResults.chromeInfo}</span>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-slate-200 bg-background px-6 flex gap-6 overflow-x-auto">
                  {(['summary', 'samples', 'visualizations', 'report'] as const).map((tab) => {
                    const label = tab === 'summary' ? 'Summary'
                      : tab === 'samples' ? `Samples (${activeResults.samples.length})`
                      : tab === 'visualizations' ? 'Visualizations' : 'Report';
                    return (
                      <button key={tab} onClick={() => setResultsTab(tab)}
                        className={`py-3.5 text-sm border-b-2 -mb-px shrink-0 transition-colors duration-200 ${
                          resultsTab === tab ? 'border-teal-600 text-teal-600 font-medium' : 'border-transparent text-muted-foreground/45 hover:text-muted-foreground/70'
                        }`}>{label}</button>
                    );
                  })}
                </div>

                {/* Signature + engine indicator */}
                <div className="px-6 py-2 border-b border-slate-100 bg-muted/20 flex items-center gap-2.5 flex-wrap">
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground/40">Signature</span>
                  <span className="text-xs font-semibold text-teal-700">{selectedSig?.name ?? 'PBMC COVID-19'}</span>
                  <span className="font-mono text-[9px] px-1.5 py-0.5 rounded border border-teal-200 text-teal-600/70 bg-teal-50">{selectedSig?.version ?? 'v1.2'}</span>
                  <span className="text-border">·</span>
                  <span className="font-mono text-[9px] text-muted-foreground/50">CellReveal Engine</span>
                </div>

                {/* Tab content */}
                <div className="p-6 md:p-8 bg-background">
                  <AnimatePresence mode="wait">

                    {resultsTab === 'summary' && (
                      <motion.div key="summary" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-xl overflow-hidden mb-8 bg-slate-200/60">
                          {[
                            { label: 'Cell types', value: String(selectedSig?.cellTypes ?? activeResults.cellTypes.length) },
                            { label: 'Dominant type', value: activeResults.dominantLabel, sub: activeResults.dominantSub },
                            { label: 'Engine', value: 'CellReveal' },
                            { label: 'Correlation', value: activeResults.correlation },
                          ].map((m, i) => (
                            <div key={i} className="p-5 bg-background">
                              <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-2 text-muted-foreground/40">{m.label}</p>
                              <p className="text-2xl font-bold tabular-nums text-teal-600">{m.value}</p>
                              {m.sub && <p className="text-xs mt-1 text-muted-foreground">{m.sub}</p>}
                            </div>
                          ))}
                        </div>
                        <p className="font-mono text-[10px] tracking-[0.35em] uppercase mb-5 text-muted-foreground/40">
                          Mean cell type proportions ± SD (n = {activeResults.samples.length} samples)
                        </p>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart layout="vertical"
                            data={[...activeResults.cellTypes].sort((a, b) => b.mean - a.mean).map(ct => ({
                              name: ct.name, value: +(ct.mean * 100).toFixed(1), sd: +(ct.sd * 100).toFixed(1), fill: ct.color,
                            }))}
                            margin={{ top: 4, right: 48, left: 8, bottom: 4 }}>
                            <XAxis type="number" domain={[0, 55]} tickFormatter={(v: number) => `${v}%`}
                              tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'rgba(0,0,0,0.3)' }} axisLine={false} tickLine={false} tickCount={6} />
                            <YAxis type="category" dataKey="name" width={140}
                              tick={{ fontSize: 10, fontFamily: 'monospace', fill: 'rgba(0,0,0,0.5)' }} axisLine={false} tickLine={false} />
                            <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={24}>
                              {[...activeResults.cellTypes].sort((a, b) => b.mean - a.mean).map((ct, i) => <Cell key={i} fill={ct.color} />)}
                              <ErrorBar dataKey="sd" width={4} strokeWidth={1.5} stroke="rgba(0,0,0,0.28)" direction="x" />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </motion.div>
                    )}

                    {resultsTab === 'samples' && (
                      <motion.div key="samples" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
                        <p className="font-mono text-[10px] tracking-[0.35em] uppercase mb-5 text-muted-foreground/40">Per-sample cell type proportions — stacked</p>
                        <ResponsiveContainer width="100%" height={Math.max(280, activeResults.samples.length * 22)}>
                          <BarChart layout="vertical" data={activeResults.samples} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                            <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`}
                              tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'rgba(0,0,0,0.3)' }} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey="name" width={56}
                              tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'rgba(0,0,0,0.45)' }} axisLine={false} tickLine={false} />
                            {activeResults.cellTypes.map((ct, i) => (
                              <Bar key={ct.key} dataKey={ct.key} stackId="a" fill={ct.color} maxBarSize={16}
                                radius={i === activeResults.cellTypes.length - 1 ? [0, 3, 3, 0] : [0, 0, 0, 0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
                          {activeResults.cellTypes.map(ct => (
                            <div key={ct.key} className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ct.color }} />
                              <span className="text-[10px] font-mono text-muted-foreground">{ct.name}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {resultsTab === 'visualizations' && (
                      <motion.div key="viz" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
                        <p className="font-mono text-[10px] tracking-[0.35em] uppercase mb-5 text-muted-foreground/40">Proportion heatmap · per-column normalized · samples clustered</p>
                        <div className="overflow-x-auto pb-2">
                          <div style={{ display: 'grid', gridTemplateColumns: `4px 60px repeat(${activeResults.cellTypes.length}, minmax(36px, 1fr))`, gap: '1px', marginBottom: '4px' }}>
                            <div /><div />
                            {activeResults.hmHeaders.map(h => (
                              <div key={h} className="text-center text-[9px] font-mono uppercase tracking-wide pb-1 text-muted-foreground/55">{h}</div>
                            ))}
                          </div>
                          {activeResults.clusters.map((cn, ci) => (
                            <div key={cn} className={ci > 0 ? 'mt-2' : ''}>
                              {activeResults.samples.filter(s => s.cluster === cn).map((s, si) => {
                                const cols = activeResults.cellTypes.map(ct => ({ v: (s as Record<string, number>)[ct.key] ?? 0, k: ct.key }));
                                return (
                                  <motion.div key={s.name}
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                    transition={{ delay: (ci * 5 + si) * 0.022 }}
                                    style={{ display: 'grid', gridTemplateColumns: `4px 60px repeat(${activeResults.cellTypes.length}, minmax(36px, 1fr))`, gap: '1px', marginBottom: '1px' }}>
                                    <div className="rounded-[2px]" style={{ backgroundColor: CLUSTER_COLORS[cn] }} />
                                    <div className="flex items-center text-[9px] font-mono pr-1 text-muted-foreground/60">{s.name}</div>
                                    {cols.map(({ v, k }) => (
                                      <div key={k} className="h-[25px] flex items-center justify-center rounded-[2px] text-[8px] font-mono tabular-nums"
                                        style={{ backgroundColor: hmBg(v, activeResults.hmColMax[k] ?? 1), color: hmText(v, activeResults.hmColMax[k] ?? 1) }}>
                                        {v.toFixed(1)}
                                      </div>
                                    ))}
                                  </motion.div>
                                );
                              })}
                            </div>
                          ))}
                          <div className="flex items-center gap-3 mt-5">
                            <span className="text-[9px] font-mono text-muted-foreground/40">low</span>
                            <div className="h-3 w-28 rounded-sm" style={{ background: 'linear-gradient(to right, hsl(182, 0%, 97%), hsl(182, 72%, 27%))' }} />
                            <span className="text-[9px] font-mono text-muted-foreground/40">high</span>
                            <span className="text-[9px] font-mono ml-1 text-muted-foreground/30">(per column)</span>
                          </div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3">
                            {activeResults.clusters.map(c => (
                              <div key={c} className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CLUSTER_COLORS[c] }} />
                                <span className="text-[9px] font-mono text-muted-foreground/55">{activeResults.clusterLabels[c]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {resultsTab === 'report' && (
                      <motion.div key="report" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="max-w-xl">
                        <p className="font-mono text-[10px] tracking-[0.35em] uppercase mb-6 text-muted-foreground/40">Downloadable report preview</p>
                        <div className="rounded-xl border border-slate-200 p-6 bg-muted/20">
                          <div className="text-xs font-mono tracking-widest uppercase mb-5 text-muted-foreground/40">CellReveal — Analysis Report</div>
                          {activeResults.reportItems.map((item, i) => (
                            <motion.div key={item.section}
                              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                              className="flex items-start gap-4 py-3.5 border-b border-slate-200/50 last:border-0">
                              <span className="font-mono text-[10px] shrink-0 pt-0.5 text-muted-foreground/35">{item.section}</span>
                              <div>
                                <p className="text-sm font-medium text-foreground">{item.title}</p>
                                <p className="text-xs mt-0.5 text-muted-foreground">{item.sub}</p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        <div className="mt-4 flex gap-2">
                          {['PDF Report', 'CSV Export', 'JSON Data'].map(fmt => (
                            <div key={fmt} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-teal-200/60 text-teal-600/70 bg-teal-50/60">
                              <DownloadSimple className="w-3.5 h-3.5" weight="bold" />{fmt}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>
              </div>

              {/* CTA */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.45 }}
                className="mt-12 rounded-2xl bg-teal-900 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <SealCheck className="w-5 h-5 text-teal-400" weight="fill" />
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-teal-400">End of walkthrough</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-teal-50 tracking-tight mb-2">Ready to run this on your data?</h3>
                  <p className="text-teal-200 text-sm max-w-sm leading-relaxed">
                    CellReveal is currently onboarding early research partners. Request access and we'll be in touch.
                  </p>
                </div>
                <div className="flex flex-col gap-2.5 shrink-0">
                  <a href="mailto:hello@cellreveal.com"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-teal-900 font-semibold rounded-xl hover:bg-teal-50 transition-colors active:scale-[0.98] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.3)]">
                    Request early access<ArrowRight className="w-4 h-4" weight="bold" />
                  </a>
                  <Link to="/"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-teal-600/60 text-teal-200 text-sm rounded-xl hover:border-teal-500 hover:text-white transition-colors">
                    Back to home
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          )}

        </AnimatePresence>

        {/* Navigation */}
        {currentStep < 4 && (
          <div className="mt-10 flex items-center justify-between">
            <button onClick={() => setCurrentStep(prev => prev - 1)} disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-muted-foreground hover:border-slate-300 hover:text-foreground transition-colors disabled:opacity-30 disabled:pointer-events-none bg-white">
              <ArrowLeft className="w-3.5 h-3.5" weight="bold" />Back
            </button>
            <div className="flex items-center gap-3">
              {!canContinue() && (
                <p className="text-xs text-muted-foreground/50 hidden sm:block">
                  {currentStep === 1 && 'Select a dataset to continue'}
                  {currentStep === 2 && 'Select a signature to continue'}
                  {currentStep === 3 && 'Run the analysis to continue'}
                </p>
              )}
              <motion.button onClick={() => setCurrentStep(prev => prev + 1)} disabled={!canContinue()} whileTap={{ scale: 0.97 }}
                className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-30 disabled:pointer-events-none shadow-[0_4px_16px_-4px_rgba(20,184,166,0.4)]">
                Continue<ArrowRight className="w-3.5 h-3.5" weight="bold" />
              </motion.button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function StepHeader({ step, title, desc }: { step: string; title: string; desc: string }) {
  return (
    <div className="max-w-2xl">
      <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-teal-500/70 block mb-3">Step {step}</span>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-3">{title}</h1>
      <p className="text-base text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}
